import { useMemo } from "react";
import HistoryIcon from "@mui/icons-material/History";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PublicIcon from "@mui/icons-material/Public";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { cls } from "../../hatim/soft/SoftKit";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useListNotificationsQuery } from "../../../api/notificationApi";
import { useGetManagedGroupsQuery } from "../../../api/managementApi";
import { useGetManagedHatimTemplatesQuery } from "../../../api/hatimManagementApi";
import { statusLabelKey, resolveTargetLabel } from "../../../lib/notificationOptions";
import ConnectionState from "../../../components/ui/ConnectionState";

const KIND_KEYS = {
  announcement: "notif_kind_announcement",
  hatim_assigned: "notif_kind_hatim_assigned",
  group_joined: "notif_kind_group_joined",
  goal_updated: "notif_kind_goal_updated",
  hatim_period: "notif_kind_hatim_period",
};

const STATUS_CLASS = {
  done: "bg-terakkiSuccess/10 text-terakkiSuccess border-terakkiSuccess/25 dark:text-[#7BE3A8]",
  failed: "bg-terakkiError/10 text-terakkiError border-terakkiError/25 dark:text-[#F2A59C]",
  sending: "bg-terakkiPrimary/10 text-terakkiPrimaryDark border-terakkiPrimary/25 dark:text-[#8FC9CA]",
  pending: "bg-terakkiSurfaceAlt text-terakkiNeutral border-terakkiBorder dark:bg-white/[0.08] dark:text-white/70 dark:border-white/[0.14]",
};

const TARGET_ICON = { group: GroupsIcon, hatim: MenuBookIcon, all: PublicIcon };

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("tr-TR");
}

export default function NotificationHistoryList() {
  const { t } = useScriptMode();
  const { data, isLoading, isFetching, isError, error, refetch } = useListNotificationsQuery({ limit: 50 });
  const { data: groupsResponse } = useGetManagedGroupsQuery();
  const { data: templatesResponse } = useGetManagedHatimTemplatesQuery();

  const groups = useMemo(
    () => groupsResponse?.data?.managedGroups || [],
    [groupsResponse]
  );
  const templates = useMemo(() => templatesResponse?.data?.templates || [], [templatesResponse]);
  const notifications = data?.data?.notifications || [];

  const header = (
    <div className="flex items-center gap-2.5 px-4 py-3 bg-terakkiBrandCloud border-b border-terakkiBrandMist dark:bg-white/[0.06] dark:border-white/[0.14]">
      <span className="w-9 h-9 rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0 dark:bg-white/10 dark:text-[#8FC9CA]">
        <HistoryIcon aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-lg font-SemiBold text-terakkiPrimaryDark leading-tight dark:text-white">
          {t("notif_history_heading")}
        </div>
        <div className="text-xs text-terakkiNeutral leading-tight dark:text-white/70">
          {notifications.length} {t("notif_kind_announcement").toLowerCase()}
        </div>
      </div>
    </div>
  );

  let content;
  if (isLoading) {
    content = (
      <div className="p-4 flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-terakkiSurfaceAlt animate-pulse dark:bg-white/[0.08]" />
        ))}
      </div>
    );
  } else if (isError) {
    content = (
      <ConnectionState
        error={error}
        onRetry={refetch}
        retrying={isFetching}
        message={t("notif_history_load_error")}
        compact
      />
    );
  } else if (notifications.length === 0) {
    content = (
      <div className="p-8 text-center">
        <HistoryIcon sx={{ fontSize: 32 }} className="text-terakkiBorder" aria-hidden />
        <p className="text-base text-terakkiNeutral mt-3 dark:text-white/70">{t("notif_history_empty")}</p>
      </div>
    );
  } else {
    content = (
      <ul className="tk-stagger overflow-y-auto flex-1 min-h-0">
        {notifications.map((item) => {
          const target = resolveTargetLabel({ audience: item.audience, groups, templates });
          const TargetIcon = TARGET_ICON[item.audience?.type] || GroupsIcon;
          const status = item.status || "pending";
          return (
            <li
              key={item._id}
              className="px-4 py-3.5 border-b border-terakkiBorder transition-colors hover:bg-terakkiBrandCloud dark:border-white/[0.14] dark:hover:bg-white/[0.06]"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-base font-SemiBold text-terakkiInk leading-snug min-w-0 dark:text-white">
                  {item.title}
                </p>
                <span
                  className={`shrink-0 text-[11px] font-SemiBold px-2.5 py-1 rounded-lg border ${
                    STATUS_CLASS[status] || STATUS_CLASS.pending
                  }`}
                >
                  {t(statusLabelKey(status))}
                </span>
              </div>

              <p className="text-sm text-terakkiNeutral mt-1 leading-snug dark:text-white/70">{item.body}</p>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5">
                {target && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-SemiBold text-terakkiPrimaryDark bg-terakkiBrandCloud border border-terakkiBrandMist rounded-lg px-2 py-1 dark:text-[#8FC9CA] dark:bg-white/[0.06] dark:border-white/[0.14]">
                    <TargetIcon sx={{ fontSize: 15 }} aria-hidden />
                    {target.labelKey ? t(target.labelKey) : target.label}
                  </span>
                )}
                <span className="text-xs text-terakkiNeutral dark:text-white/70">
                  {t(KIND_KEYS[item.kind] || KIND_KEYS.announcement)}
                </span>
                <span className="text-xs text-terakkiNeutral dark:text-white/70">·</span>
                <span className="inline-flex items-center gap-1 text-xs text-terakkiNeutral dark:text-white/70">
                  <PhoneIphoneIcon sx={{ fontSize: 14 }} aria-hidden />
                  {item.stats?.sent || 0}/{item.stats?.tokens || 0} {t("notif_devices")}
                </span>
                <span className="text-xs text-terakkiNeutral ml-auto dark:text-white/70">
                  {formatDate(item.createdAt)}
                </span>
              </div>

              {(item.stats?.recipients > 0 || item.stats?.failed > 0 || item.error) && (
                <div className="mt-2 pt-2 border-t border-terakkiBorder flex flex-wrap items-center gap-x-3 gap-y-1 dark:border-white/[0.14]">
                  {item.stats?.recipients > 0 && (
                    <span className="text-xs text-terakkiNeutral dark:text-white/70">
                      {t("notif_stat_recipients")}: {item.stats.recipients}
                    </span>
                  )}
                  {item.stats?.failed > 0 && (
                    <span className="text-xs font-SemiBold text-terakkiError dark:text-[#F2A59C]">
                      {t("notif_stat_failed")}: {item.stats.failed}
                    </span>
                  )}
                  {item.error && (
                    <span className="text-xs text-terakkiError break-all dark:text-[#F2A59C]">
                      {t("notif_error_label")}: {item.error}
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  // Kart, yanındaki "Yeni duyuru" kartıyla aynı yüksekliğe gerilir (ızgara
  // satırı ikisini eşitler); başlık sabit kalır, liste kendi içinde kayar.
  // Tek sütuna düşen dar ekranlarda gerilecek bir komşu olmadığı için
  // yükseklik ekrana göre sınırlanıyor, sayfa sonsuza uzamasın.
  return (
    <div className={`${cls.panel} overflow-hidden flex flex-col h-full max-h-[70vh] lg:max-h-none`}>
      <div className="shrink-0">{header}</div>
      <div className="min-h-0 flex-1 flex flex-col">{content}</div>
    </div>
  );
}
